import React, { useState, useEffect, useCallback } from 'react';
// Mocking useSearchParams for standalone execution and URL handling
// In a real app, this would be: import { useSearchParams } from 'react-router-dom';
const useSearchParams = () => {
    // === CONFIGURATION: Change 'token=' value here to test different states ===
    // 'valid-share-token-123' -> Success case (Needs Approval)
    // 'secure-token-456' -> Requires Auth error
    // 'simple-form-token-321' -> Simple form (No approval needed by default)
    // 'expired-token-789' -> Expired link error
    const mockParams = new URLSearchParams('?token=valid-share-token-123&recipient=user-email-hash');
    return [mockParams];
};


// --- INLINED MOCK API UTILITY FUNCTIONS ---

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// 1. Shared Resource Metadata
const mockSharedResources = {
    'valid-share-token-123': {
        resourceId: 'FORM-001-APP',
        resourceType: 'Form',
        isPublic: true,
        requiresAuth: false,
        expiresAt: new Date(Date.now() + 86400000 * 30).toISOString(),
        ownerId: 'user-a1b2c3',
    },
    'secure-token-456': {
        resourceId: 'FORM-002-SEC',
        resourceType: 'Form',
        isPublic: false,
        requiresAuth: true,
        expiresAt: null,
        ownerId: 'user-x9y8z7',
    },
    'simple-form-token-321': {
        resourceId: 'FORM-003-SIMPLE',
        resourceType: 'Form',
        isPublic: true,
        requiresAuth: false,
        expiresAt: null,
        ownerId: 'user-simple',
    },
    'expired-token-789': {
        resourceId: 'FORM-001-APP',
        resourceType: 'Form',
        isPublic: true,
        requiresAuth: false,
        expiresAt: new Date(Date.now() - 86400000).toISOString(),
        ownerId: 'user-a1b2c3',
    }
};

// 2. Form Configuration/Structure
const mockFormStructures = {
    'FORM-001-APP': {
        id: 'FORM-001-APP',
        title: 'Application for Student Research Grant',
        description: 'Please fill out this form to apply for the annual Vidyazon Research Grant. All submissions require review by the Grants Committee.',
        fields: [
            { id: 'fullName', label: 'Full Name', type: 'text', required: true, placeholder: 'Jane Doe' },
            { id: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'jane@example.com' },
            { id: 'projectTitle', label: 'Project Title', type: 'text', required: true, placeholder: 'The Future of AI in Education' },
            { id: 'abstract', label: 'Project Abstract (Max 500 words)', type: 'textarea', required: true, placeholder: 'Summarize your project concisely.' },
            { id: 'fundingAmount', label: 'Requested Funding (USD)', type: 'number', required: true, placeholder: '5000' },
        ],
    },
    'FORM-002-SEC': { id: 'FORM-002-SEC', title: 'Secure Internal Review Form', description: 'Authentication required.', fields: [] },
    'FORM-003-SIMPLE': {
        id: 'FORM-003-SIMPLE',
        title: 'Quick Feedback Survey',
        description: 'Your feedback helps us improve our services. Thank you for your input.',
        fields: [
            { id: 'serviceRating', label: 'Service Rating (1-5)', type: 'number', required: true, placeholder: '4' },
            { id: 'comment', label: 'General Comments', type: 'textarea', required: false, placeholder: 'Any suggestions?' },
        ],
    }
};

const fetchSharedResource = async (shareToken) => {
    await delay(500);
    const resource = mockSharedResources[shareToken];
    if (!resource) throw new Error("Invalid or non-existent share link.");
    if (resource.expiresAt && new Date(resource.expiresAt) < new Date()) {
        throw new Error("This form link has expired.");
    }
    return resource;
};

const fetchFormData = async (formId) => {
    await delay(300);
    const formStructure = mockFormStructures[formId];
    if (!formStructure) throw new Error(`Form structure for ID ${formId} not found.`);
    return { formStructure };
};

const submitForm = async (formId, formData, shareToken, recipientToken, options = {}) => {
    await delay(1000);
    console.log(`Submission Received. Needs Approval: ${!!options.needsApproval}`);
    if (formData.projectTitle === 'Error') {
         throw new Error("Mock Error: Submission was rejected due to inappropriate title.");
    }
    return { submissionId: `SUB-${Date.now()}`, status: 'Pending Review', requiresReview: !!options.needsApproval };
};

// --- Supporting Components ---

const Logo = () => (
    <div className="text-3xl font-extrabold text-indigo-600 tracking-tight">
        Vidyazon <span className="text-base font-normal text-gray-500">Forms</span>
    </div>
);

const Center = ({ children, className = "", style = {} }) => (
    <div 
        className={`flex justify-center items-center ${className}`}
        style={style}
    >
        {children}
    </div>
);

// FormRenderer: Dynamically renders form fields and manages local state
const FormRenderer = ({ config, onSubmit, isSubmitting, submitButtonText }) => {
    // Initialize form data based on configuration
    const initialData = config.reduce((acc, field) => ({ ...acc, [field.id]: '' }), {});
    const [formData, setFormData] = useState(initialData);

    // Optimized change handler using useCallback
    const handleChange = useCallback((e) => {
        const { id, value, type } = e.target;
        // Basic type conversion for numbers
        const finalValue = type === 'number' ? (value === '' ? '' : Number(value)) : value;
        setFormData(prev => ({ ...prev, [id]: finalValue }));
    }, []);

    const handleSubmitLocal = (e) => {
        e.preventDefault();
        
        // Basic required field validation
        const missingFields = config.filter(field => field.required && !formData[field.id]);
        if (missingFields.length > 0) {
            // Using a custom message instead of alert()
            document.getElementById('form-error-message').innerText = 
                `Please complete all required fields: ${missingFields.map(f => f.label).join(', ')}`;
            return;
        }
        
        // Clear any previous error message
        document.getElementById('form-error-message').innerText = '';
        onSubmit(formData);
    };

    const renderInput = (field) => {
        const commonProps = {
            id: field.id,
            name: field.id,
            value: formData[field.id] || '',
            onChange: handleChange,
            required: field.required,
            placeholder: field.placeholder || field.label,
            className: "mt-1 block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition duration-150 ease-in-out",
            disabled: isSubmitting,
            min: field.type === 'number' ? 1 : undefined,
            max: field.type === 'number' ? 5 : undefined,
        };

        if (field.type === 'textarea') {
            return <textarea {...commonProps} rows="4" />;
        }
        
        return <input {...commonProps} type={field.type} />;
    };

    return (
        <form onSubmit={handleSubmitLocal} className="space-y-6">
            <div id="form-error-message" className="text-red-600 text-sm font-medium mb-4"></div>
            {config.map(field => (
                <div key={field.id} className="w-full">
                    <label htmlFor={field.id} className="block text-sm font-medium text-gray-700">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {renderInput(field)}
                </div>
            ))}
            <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300 ease-in-out disabled:bg-indigo-400 disabled:cursor-not-allowed transform hover:scale-[1.01] active:scale-[0.99]"
            >
                {isSubmitting ? 'Submitting...' : submitButtonText}
            </button>
        </form>
    );
};


/**
 * PublicFormAccess Component
 * Displays a shared form with a minimalist layout.
 */
const PublicFormAccess = () => {
    const [searchParams] = useSearchParams();
    const [shareToken] = useState(searchParams.get('token'));
    const [recipientToken] = useState(searchParams.get('recipient'));
    
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formConfig, setFormConfig] = useState(null);
    const [sharedResource, setSharedResource] = useState(null);
    const [submissionStatus, setSubmissionStatus] = useState('idle'); // 'idle', 'pending', 'success', 'error'

    // --- 1. Fetch Shared Resource and Form Data ---
    useEffect(() => {
        // Reset status when token changes
        setError(null);
        setSubmissionStatus('idle');

        if (!shareToken) {
            setError("Share token is missing from the URL.");
            setIsLoading(false);
            return;
        }

        const loadForm = async () => {
            try {
                // Fetch the shared resource metadata
                const accessResponse = await fetchSharedResource(shareToken);

                // --- Handle Secure Access ---
                if (!accessResponse.isPublic && accessResponse.requiresAuth) {
                    setError("🔐 This form requires authentication. Please log in.");
                    setIsLoading(false);
                    return;
                }
                
                // Fetch the actual form data using resourceId
                const formId = accessResponse.resourceId;
                const formResponse = await fetchFormData(formId);

                setSharedResource(accessResponse);
                setFormConfig(formResponse.formStructure);

            } catch (err) {
                setError(err.message || "Failed to load the form. Check token or expiry.");
            } finally {
                setIsLoading(false);
            }
        };

        loadForm();
    }, [shareToken, recipientToken]);


    // --- 2. Handle Form Submission with Approval Requirement ---
    const handleSubmit = async (formData) => {
        if (!formConfig || !sharedResource) return;

        setSubmissionStatus('pending');
        setError(null); 
        
        // Determine if approval is explicitly required for this specific form type
        // In the original component, it was always set to true, so we keep that logic 
        // for the intended grant application flow.
        const needsApproval = true; 
        
        try {
            await submitForm(
                formConfig.id, 
                formData, 
                shareToken, 
                recipientToken, 
                { needsApproval: needsApproval }
            );

            setSubmissionStatus('success');

        } catch (err) {
            setSubmissionStatus('error');
            setError(err.message || "Submission failed. Please try again.");
        }
    };

    // --- 3. Rendering Logic ---

    // Global Styles and Layout
    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            <script src="https://cdn.tailwindcss.com"></script>
            <style jsx="true">{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
                .font-sans { font-family: 'Inter', sans-serif; }
                .form-container {
                    display: flex;
                    flex-direction: column;
                    min-height: 100vh;
                }
                .logo-header {
                    padding: 20px 40px;
                    border-bottom: 1px solid #e5e7eb;
                    background-color: #ffffff;
                }
                .form-content-wrapper {
                    flex-grow: 1;
                    display: flex;
                    justify-content: center;
                    align-items: flex-start; /* Align content to the top */
                    padding: 20px;
                }
                .form-content {
                    padding: 40px;
                    max-width: 700px;
                    width: 100%;
                    background-color: #ffffff;
                    border-radius: 12px;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                    margin-top: 20px;
                }
                @media (max-width: 768px) {
                    .form-content {
                        padding: 20px;
                        margin-top: 0;
                        box-shadow: none;
                        border-radius: 0;
                    }
                }
            `}</style>

            <header className="logo-header">
                <Logo /> 
            </header>

            <div className="form-content-wrapper">
                {isLoading && (
                    <Center className="h-64">
                        <div className="animate-pulse text-xl text-gray-500">
                            Loading Form Structure...
                        </div>
                    </Center>
                )}

                {!isLoading && error && (
                    <div className="form-content">
                        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold mb-3">🚫 Access Error</h2>
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                {!isLoading && !error && formConfig && submissionStatus !== 'success' && (
                    <main className="form-content">
                        <h1 className="text-3xl font-extrabold text-gray-800 mb-2">{formConfig.title}</h1>
                        <p className="text-gray-500 mb-8">{formConfig.description}</p>
                        
                        {/* Display submission error if status is error */}
                        {submissionStatus === 'error' && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                                Submission failed: {error}
                            </div>
                        )}

                        <FormRenderer 
                            config={formConfig.fields} 
                            onSubmit={handleSubmit} 
                            isSubmitting={submissionStatus === 'pending'}
                            submitButtonText="Submit for Review"
                        />
                        
                        {sharedResource.expiresAt && (
                            <p className="mt-6 text-sm text-center text-red-600 font-medium p-2 bg-red-50 rounded-lg">
                                ⚠️ This form expires on {new Date(sharedResource.expiresAt).toLocaleDateString()}.
                            </p>
                        )}
                    </main>
                )}
                
                {!isLoading && submissionStatus === 'success' && (
                    <main className="form-content text-center py-20">
                        <h2 className="text-5xl mb-4">🎉</h2>
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">Thank You for Your Submission!</h2>
                        <p className="text-lg text-gray-600 max-w-md mx-auto">
                            Your application has been successfully received. 
                            <br/>
                            <span className="font-semibold text-indigo-600">It requires administrative approval before being published.</span> You will be notified once a decision has been made.
                        </p>
                    </main>
                )}
            </div>
            
            <footer className="w-full text-center py-4 text-sm text-gray-500 border-t border-gray-200 mt-auto">
                <p>&copy; {new Date().getFullYear()} Vidyazon. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default PublicFormAccess;